import { Backdrop, Box, Button, Card, CardContent, CardHeader, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, makeStyles } from '@material-ui/core';
import { RegistrationResponseJSON } from '@simplewebauthn/types';
import QRCode from 'qrcode';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { SiteContext, StudentContext, UserContext } from '../../contexts';
import { MFAConfig } from '../../dto';
import UserHelper from '../../helpers/userHelper';
import { useDialogController, useGenerateRegistrationOptions, useSendAccountSetupEmail, useUpdateUserMfa, useVerifyPkRegistration } from '../../hooks';
import useGenerateTotpUri from '../../hooks/gql/useGenerateTotpUri';
import { LanguageString, MessageDialog } from '../Common';
import { DynamicForm } from '../DynamicForm';
import ChangePasswordDialog from './ChangePasswordDialog';

type PKExtendedWindow = { SimpleWebAuthnBrowser: { startRegistration: (options: PublicKeyCredentialCreationOptions, skipAttestation?: boolean) => Promise<RegistrationResponseJSON> } };
const swab = (window as unknown as PKExtendedWindow).SimpleWebAuthnBrowser;
const { startRegistration } = swab ? swab : { startRegistration: async () => { throw new Error('SimpleWebAuthnBrowser not available'); } };

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: `${theme.spacing(2)}px`
  },
  [theme.breakpoints.up('md')]: {
    container: {
      gridTemplateColumns: '1fr 3fr'
    }
  }
}));

export const Profile: React.FC = () => {
  const { site } = useContext(SiteContext);
  const studentCtx = useContext(StudentContext);
  const userCtx = useContext(UserContext);
  const [updateUserMfa] = useUpdateUserMfa();
  const [sendAccountSetupEmail] = useSendAccountSetupEmail();
  const [generateTotpUri] = useGenerateTotpUri();
  const [generateRegistrationOptions] = useGenerateRegistrationOptions();
  const [verifyPkRegistration] = useVerifyPkRegistration();
  const msgCtrl = useDialogController(false);
  const confirmSetupEmailCtrl = useDialogController(false);
  const [isSaving, setIsSaving] = useState(false);
  const classes = useStyles();
  const isAdvisor = userCtx.user ? UserHelper.isAdvisorOrAdmin(userCtx.user) : false;
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [totpDialogOpen, setTotpDialogOpen] = useState(false);
  const [totpUri, setTotpUri] = useState<string>('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ownProfile = studentCtx?.student.id === userCtx.user?.id;
  const formId = (isAdvisor && !ownProfile ? site?.advisorProfileForm?.id : site?.studentProfileForm?.id) ?? 0;
  const mfaConfig = studentCtx?.student.mfaConfig;

  const toggleChangePasswordModal = () => {
    setChangePasswordModalOpen(!changePasswordModalOpen);
  };

  const reRegisterUser = async () => {
    if (studentCtx?.student.id) {
      await sendAccountSetupEmail({ variables: { input: { userId: studentCtx?.student.id } } });
      confirmSetupEmailCtrl.controller.setOpen();
    }
  };

  const handleFormSaving = (saving: boolean) => {
    setIsSaving(saving);
  };

  const handleFormSaved = () => {
    setIsSaving(false);
    msgCtrl.controller.setOpen();
  };

  const handleEnableMfaClick = async () => {
    setIsSaving(true);
    try {
      await updateUserMfa({ variables: { input: { enableMfa: !studentCtx?.student.twoFactorEnabled } } });
    } catch (error) {
      console.error('Error enabling MFA:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetSmsOtpClick = async (enable: boolean) => {
    setIsSaving(true);
    try {
      await updateUserMfa({ variables: { input: { enableSmsOTP: enable } } });
    } catch (error) {
      console.error('Error updating SMS OTP setting:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateTotpClick = async (mfaConfig: MFAConfig) => {
    setIsSaving(true);
    try {
      if (mfaConfig.totpEnabled) {
        await updateUserMfa({ variables: { input: { addTotp: false } } });
      } else {
        await updateUserMfa({ variables: { input: { addTotp: true } } });
        const { data } = await generateTotpUri();
        if (data?.generateTotpUri) {
          setTotpUri(data.generateTotpUri);
          setTotpDialogOpen(true);
        }
      }
    } catch (error) {
      console.error('Error generating TOTP URI:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseTotpDialog = () => {
    setTotpDialogOpen(false);
    setTotpUri('');
  };

  const handleGenerateRegistrationOptionsClick = async () => {
    const { data } = await generateRegistrationOptions();
    if (data?.generateRegistrationOptions == null) throw new Error('No registration options received');

    const opts = data?.generateRegistrationOptions as unknown as PublicKeyCredentialCreationOptions;
    startRegistration(opts, true).then(async (attResp: RegistrationResponseJSON) => {

      verifyPkRegistration({ variables: { input: { registrationResponse: JSON.stringify(attResp) } } }).then(({ data }) => {
        console.log('Passkey registration verification result:', data);
      }).catch((err: unknown) => {
        console.error('Error verifying PK registration:', err);
      });

    }).catch((err: unknown) => {
      console.error('Error during registration:', err);
    });
  };

  const mfaOptions = (mfaConfig: MFAConfig | undefined) => {
    if (mfaConfig == null) return null;

    const twoFactorEnabled = studentCtx?.student.twoFactorEnabled;
    const canEnableSmsOtp = mfaConfig?.smsOTP !== 0 && site?.publicCustomPropertyTypes.find(c => c.name === 'smsOtpEnabled') != null;
    const smsOtpEnabled = mfaConfig.smsOtpEnabled;
    const showMfaButton = mfaConfig.emailOTP === 1 || !mfaConfig.enabled;
    const showSmsButton = canEnableSmsOtp && (mfaConfig.smsOTP === 1 || !smsOtpEnabled);
    const showTotpButton = mfaConfig.totp === 1 || (mfaConfig.totp === 2 && !mfaConfig.totpEnabled);
    const showPasskeyButton = mfaConfig.passkey === 1 || (mfaConfig.passkey === 2 && !mfaConfig.passkeyEnabled);

    return twoFactorEnabled
      ? (<>
        {showMfaButton && <Button variant="contained" color="primary" onClick={handleEnableMfaClick}>{'Disable MFA'}</Button>}
        {showSmsButton && <Button variant="contained" color="primary" onClick={() => handleSetSmsOtpClick(!smsOtpEnabled)}>{smsOtpEnabled ? 'Disable SMS OTP' : 'Enable SMS OTP'}</Button>}
        {showTotpButton && <Button variant="contained" color="primary" onClick={() => handleGenerateTotpClick(mfaConfig)}>{mfaConfig.totpEnabled ? 'Disable Authenticator App' : 'Enable Authenticator App'}</Button>}
        {showPasskeyButton && <Button variant="contained" color="primary" onClick={() => handleGenerateRegistrationOptionsClick()}>{mfaConfig.passkeyEnabled ? 'Delete Passkey' : 'Register Passkeys'}</Button>}
      </>)
      : <Button variant="contained" color="primary" onClick={handleEnableMfaClick}>{'Enable Two-Factor Authentication'}</Button>;
  };

  useEffect(() => {
    if (totpDialogOpen && totpUri) {
      const timer = setTimeout(() => {
        if (canvasRef.current) {
          QRCode.toCanvas(canvasRef.current, totpUri, { width: 256 }, (error) => {
            if (error) console.error('Error generating QR code:', error);
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [totpDialogOpen, totpUri, canvasRef.current]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={<LanguageString groupName="SITE_CONENT" resourceName="PROFILE_INFORMATION" defaultText="Profile" />} />
          <CardContent>
            <DynamicForm
              formId={formId}
              userId={studentCtx?.student.id ?? 0}
              autoloadFormSubmission={true}
              shouldScrollToTopOnSubmission={true}
              autoSave={false}
              submitText="Save"
              showSubmitButton={true}
              renderFormTitle={false}
              disableCache={true}
              onIsSubmittingChange={handleFormSaving}
              onFormSubmitted={handleFormSaved}
            />
          </CardContent>
        </Card>
      </Grid>
      {studentCtx?.student && userCtx.user && ownProfile && (
        <Grid item xs={12}>
          <Card>
            <CardHeader title={<LanguageString groupName="SITE_CONENT" resourceName="SECURITY_INFORMATION" defaultText="Security" />} />
            <Box display="flex" gridGap="8px" clone>
              <CardContent>
                <Button variant="contained" color="primary" onClick={toggleChangePasswordModal}>
                  <LanguageString groupName="SITE_CONENT" resourceName="CHANGE_PASSWORD" defaultText="Change Password" />
                </Button>
                {mfaOptions(mfaConfig)}
              </CardContent>
            </Box>
          </Card>
          <ChangePasswordDialog open={changePasswordModalOpen} toggle={toggleChangePasswordModal} />
        </Grid>
      )}
      {isAdvisor && studentCtx?.student && studentCtx.student.id !== userCtx.user.id && (site?.accountSetupProcess || site?.settings?.find(s => s.key === 'SHOW_SEND_SETUP_EMAIL' && s.value === 'true')) && (
        <Grid item xs={12}>
          <Card>
            <CardHeader title={<LanguageString groupName="SITE_CONENT" resourceName="SECURITY_ACCOUNTSETUP" defaultText="Account Setup" />} />
            <CardContent>
              <Button variant="contained" color="primary" onClick={reRegisterUser} style={{ marginLeft: '10px' }}>
                <LanguageString groupName="SITE_CONENT" resourceName="REREGISTER_USER" defaultText="Send Account Setup Email" />
              </Button>
            </CardContent>
          </Card>
        </Grid>
      )}
      <MessageDialog ctrl={msgCtrl} message={<LanguageString groupName="FORM" resourceName="PROFILE_SAVED" />} />
      <MessageDialog ctrl={confirmSetupEmailCtrl} message={<LanguageString groupName="FORM" resourceName="SETUP_EMAIL_SENT" />} />
      <Dialog open={totpDialogOpen} onClose={handleCloseTotpDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <LanguageString groupName="SITE_CONENT" resourceName="TOTP_SETUP" defaultText="Scan QR Code with Authenticator App" />
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px' }}>
          <canvas ref={canvasRef} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTotpDialog} color="primary">
            <LanguageString groupName="COMMON" resourceName="CLOSE" defaultText="Close" />
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="primary" />
      </Backdrop>
    </Grid>
  );
};
export default Profile;
